import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, Calendar, Mail, Phone, ArrowRight, BookOpen, Award } from "lucide-react";
import { SITE } from "@/lib/site";
import { ScheduleConsultationButton } from "@/components/site/PageHero";

export default function TaxSchoolWelcome() {
  const { state } = useLocation();
  const enrollment = state?.enrollment;

  return (
    <div data-testid="tax-school-welcome-page">
      {/* CONFIRMATION HERO */}
      <section className="relative bg-trj-navy-gradient text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full" style={{background:"radial-gradient(closest-side, rgba(212,175,55,0.35), transparent)"}}/>
        </div>
        <div className="relative max-w-4xl mx-auto px-6 py-24 md:py-32 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--trj-gold)] text-[var(--trj-navy)] mb-6">
            <CheckCircle2 className="w-8 h-8" strokeWidth={2.5} />
          </div>
          <div className="trj-eyebrow mb-4" data-testid="welcome-eyebrow">Enrollment Confirmed</div>
          <h1 className="font-serif-trj text-4xl md:text-6xl leading-[1.05] tracking-tight">
            Welcome to TRJ Tax School.
          </h1>
          <div className="trj-divider-gold mx-auto mt-6" />
          {enrollment ? (
            <p className="mt-7 text-white/80 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Thank you for enrolling in <span className="text-[var(--trj-gold)] font-semibold">{enrollment.course_title}</span>.
              Your payment of <strong>${enrollment.amount} USD</strong> has been received.
              A confirmation has been sent to your email.
            </p>
          ) : (
            <p className="mt-7 text-white/80 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Thank you for enrolling. A confirmation has been sent to your email with next steps.
            </p>
          )}

          {enrollment && (
            <div className="mt-10 bg-[var(--trj-navy)]/60 border border-white/15 backdrop-blur p-6 md:p-8 max-w-xl mx-auto text-left">
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/60">Course</div>
                  <div className="text-white font-medium mt-1">{enrollment.course_title}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/60">Tuition</div>
                  <div className="text-[var(--trj-gold)] font-serif-trj text-xl mt-1">${enrollment.amount}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/60">Order ID</div>
                  <div className="text-white/90 font-mono text-xs mt-1 break-all" data-testid="welcome-order-id">{enrollment.order_id}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/60">Status</div>
                  <div className="text-[var(--trj-green)] font-medium mt-1">{enrollment.status}</div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="gold-rule" />
      </section>

      {/* NEXT STEPS */}
      <section className="bg-white py-20 md:py-24" data-testid="welcome-next-steps">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="trj-eyebrow mb-3">Your Next Steps</div>
            <h2 className="font-serif-trj text-3xl md:text-4xl text-[var(--trj-navy)] leading-tight">
              Here&apos;s what happens now.
            </h2>
          </div>

          <ol className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              {
                step: 1,
                icon: Mail,
                title: "Check your inbox",
                description: "Your enrollment confirmation is on the way. If you don't see it within 10 minutes, check your spam folder or reach out.",
              },
              {
                step: 2,
                icon: Calendar,
                title: "Book your onboarding",
                description: "Schedule your TRJ Tax School onboarding session — we'll walk you through course access, expectations, and timeline.",
              },
              {
                step: 3,
                icon: BookOpen,
                title: "Begin your training",
                description: "You'll receive course materials, templates, and access details. Year-round instructor support is included.",
              },
            ].map((s) => (
              <li key={s.step} className="bg-[var(--trj-light)] border border-gray-200 p-7 relative">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--trj-green)] font-bold">Step 0{s.step}</div>
                <div className="w-10 h-10 bg-[var(--trj-navy)] text-[var(--trj-gold)] flex items-center justify-center mt-3">
                  <s.icon className="w-5 h-5" />
                </div>
                <div className="font-serif-trj text-xl mt-4 text-[var(--trj-navy)]">{s.title}</div>
                <p className="text-sm text-[var(--trj-muted)] mt-2 leading-relaxed">{s.description}</p>
              </li>
            ))}
          </ol>

          <div className="mt-12 flex justify-center flex-wrap gap-3">
            <ScheduleConsultationButton testid="welcome-schedule-btn" label="Book Onboarding Call" />
            <Link to="/tax-school" className="trj-btn-outline-navy" data-testid="welcome-back-btn">
              Back to Tax School
            </Link>
          </div>
        </div>
      </section>

      {/* SUPPORT */}
      <section className="bg-[var(--trj-light)] py-16" data-testid="welcome-support">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
          <div className="bg-white p-6">
            <div className="w-10 h-10 bg-[var(--trj-navy)] text-[var(--trj-gold)] flex items-center justify-center">
              <Phone className="w-4 h-4" />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--trj-muted)] mt-3">Call</div>
            <a href={SITE.phoneHref} className="text-[var(--trj-navy)] font-medium mt-1 block hover:text-[var(--trj-green)]">{SITE.phone}</a>
          </div>
          <div className="bg-white p-6">
            <div className="w-10 h-10 bg-[var(--trj-navy)] text-[var(--trj-gold)] flex items-center justify-center">
              <Mail className="w-4 h-4" />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--trj-muted)] mt-3">Email</div>
            <a href={SITE.emailHref} className="text-[var(--trj-navy)] font-medium mt-1 block hover:text-[var(--trj-green)]">{SITE.email}</a>
          </div>
          <div className="bg-white p-6">
            <div className="w-10 h-10 bg-[var(--trj-navy)] text-[var(--trj-gold)] flex items-center justify-center">
              <Award className="w-4 h-4" />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--trj-muted)] mt-3">Year-round support</div>
            <div className="text-[var(--trj-navy)] font-medium mt-1">Instructor &amp; advisor access</div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--trj-navy)] py-16 text-white text-center" data-testid="welcome-cta">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-serif-trj text-3xl md:text-4xl leading-tight">
            Need help getting started?
          </h2>
          <div className="mt-7 flex justify-center flex-wrap gap-3">
            <ScheduleConsultationButton variant="gold" testid="welcome-cta-schedule-btn" label="Schedule Consultation" />
            <Link to="/contact" className="trj-btn-outline-white" data-testid="welcome-cta-contact-btn">
              Contact Us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
